import dayjs, { Dayjs } from 'dayjs';
import {
  RoutineActivityRecurring,
  RoutineActivityRecurringDay,
  RoutineCustomRecurrence,
  RoutineRecurring,
} from '@demind-inc/core';
import { defaultRecurring } from '../AIScheduler/reducer/routinePrefItem.state';

// ── Constants ───────────────────────────────────────────────────────────────

export const DAYS_OF_WEEK: Array<{ key: RoutineActivityRecurringDay; label: string }> = [
  { key: 'sunday', label: 'S' },
  { key: 'monday', label: 'M' },
  { key: 'tuesday', label: 'T' },
  { key: 'wednesday', label: 'W' },
  { key: 'thursday', label: 'T' },
  { key: 'friday', label: 'F' },
  { key: 'saturday', label: 'S' },
];

export const DAY_FULL_LABELS: Array<{ key: RoutineActivityRecurringDay; label: string }> = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

export const WEEK_POSITIONS: Array<{ value: -1 | 1 | 2 | 3 | 4; label: string }> = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: -1, label: 'Last' },
];

export const WEEKLY_INTERVAL_OPTIONS = [1, 2, 3, 4];
export const MONTHLY_INTERVAL_OPTIONS = [1, 2, 3, 4, 6, 12];
export const MAX_MONTH_DAY = 31;

// ── Type guard ──────────────────────────────────────────────────────────────

const CUSTOM_RECURRENCE_TYPES: RoutineCustomRecurrence['type'][] = ['weekly', 'monthly-date', 'monthly-weekday'];

export const isCustomRecurring = (
  recurring: RoutineRecurring
): recurring is RoutineCustomRecurrence => {
  return (
    !!recurring &&
    typeof recurring === 'object' &&
    'type' in recurring &&
    CUSTOM_RECURRENCE_TYPES.includes(recurring.type)
  );
};

// ── Conversion helpers ──────────────────────────────────────────────────────

export const getRecurringDayFromDate = (date: Dayjs): RoutineActivityRecurringDay =>
  date.locale('en').format('dddd').toLowerCase() as RoutineActivityRecurringDay;

const getTodayDay = () => getRecurringDayFromDate(dayjs());

const ALL_DAYS = DAYS_OF_WEEK.map(({ key }) => key);

const getEnabledDays = (recurring: RoutineActivityRecurring): RoutineActivityRecurringDay[] =>
  ALL_DAYS.filter((day) => recurring[day]?.enabled);

export const toRoutineActivityRecurring = (
  recurring?: RoutineRecurring
): RoutineActivityRecurring => {
  if (!recurring) {
    return { ...defaultRecurring };
  }
  if (isCustomRecurring(recurring)) {
    return customWeeklyToLegacy(recurring);
  }
  return recurring;
};

export const customWeeklyToLegacy = (
  recurring: RoutineCustomRecurrence
): RoutineActivityRecurring => {
  const base: RoutineActivityRecurring = { ...defaultRecurring };
  const selected = recurring.type === 'weekly' ? recurring.daysOfWeek : [];

  for (const day of ALL_DAYS) {
    base[day] = { enabled: selected.includes(day) };
  }
  return base;
};

export const legacyToCustomWeekly = (
  recurring: RoutineActivityRecurring
): RoutineCustomRecurrence => {
  const enabledDays = getEnabledDays(recurring);
  return {
    type: 'weekly',
    interval: 1,
    daysOfWeek: enabledDays.length > 0 ? enabledDays : [getTodayDay()],
    startDate: dayjs().format('YYYY-MM-DD'),
  };
};

export const buildDefaultCustomRecurrence = (
  type: RoutineCustomRecurrence['type'],
  existing?: RoutineCustomRecurrence
): RoutineCustomRecurrence => {
  const startDate = existing?.startDate && dayjs(existing.startDate).isValid()
    ? existing.startDate
    : dayjs().format('YYYY-MM-DD');
  const interval = existing?.interval || 1;

  switch (type) {
    case 'weekly':
      return { type: 'weekly', interval, daysOfWeek: [getTodayDay()], startDate };
    case 'monthly-date':
      return {
        type: 'monthly-date',
        interval,
        daysOfMonth: [Math.min(dayjs().date(), MAX_MONTH_DAY)],
        startDate,
      };
    case 'monthly-weekday':
      return { type: 'monthly-weekday', interval, weekOfMonth: 1, dayOfWeek: getTodayDay(), startDate };
  }
};
